import { api } from './api-client';

/**
 * The reasons the first run offers, as `/v1/admin/motivations` serves them.
 *
 * Declared here rather than imported from the API: ADR 0001 settles that types
 * are duplicated between the API and its clients rather than shared through a
 * workspace. The shape has to keep matching `api/src/motivations/`.
 */
export interface Motivation {
  id: string;
  /** A stable slug. Events carry this, not the label. */
  code: string;
  /** What the chip says, in the learner's voice. */
  label: string;
  /** The capability the first run closes on, tied to this reason. */
  promise: string;
  /** Lower comes first. Null means unranked — it sits at the back. */
  displayOrder: number | null;
  createdAt: string;
  updatedAt: string;
}

/** What the add and edit forms produce. */
export interface MotivationDraft {
  code: string;
  label: string;
  promise: string;
  displayOrder: number | null;
}

export async function listMotivations(): Promise<Motivation[]> {
  const { motivations } = await api.get<{ motivations: Motivation[] }>(
    '/admin/motivations',
  );
  return motivations;
}

export async function createMotivation(
  draft: MotivationDraft,
): Promise<Motivation> {
  const { motivation } = await api.post<{ motivation: Motivation }>(
    '/admin/motivations',
    draft,
  );
  return motivation;
}

export async function updateMotivation(
  id: string,
  draft: MotivationDraft,
): Promise<Motivation> {
  const { motivation } = await api.patch<{ motivation: Motivation }>(
    `/admin/motivations/${id}`,
    draft,
  );
  return motivation;
}

export function deleteMotivation(id: string): Promise<void> {
  return api.del(`/admin/motivations/${id}`);
}
